import type { CompanyMembershipRole } from '../models/company.models';

/**
 * Alinha o papel devolvido pela API (.NET enums, strings PT/EN) ao modelo do front.
 *
 * Valores numéricos — `Taskeo.Domain.Enums.UserRole`: `Owner = 1`, `Admin = 2`, `Member = 3`.
 * Regra de negócio: quem **cria** a empresa é **Admin (2)**. `Owner (1)` é papel distinto para
 * casos explícitos (ex. fundador), não o default do criador.
 */
export function normalizeCompanyMembershipRole(
  raw: unknown,
  fallback: CompanyMembershipRole = 'Member'
): CompanyMembershipRole {
  /** Enum / objeto parcial vindo de JSON .NET (`{ "value": 1 }`, `{ "name": "Admin" }`, etc.). */
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const o = raw as Record<string, unknown>;
    const nested =
      o['value'] ??
      o['Value'] ??
      o['name'] ??
      o['Name'] ??
      o['role'] ??
      o['Role'];
    if (nested !== undefined && nested !== raw) {
      return normalizeCompanyMembershipRole(nested, fallback);
    }
  }
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    if (raw === 1) return 'Owner';
    if (raw === 2) return 'Admin';
    if (raw === 3) return 'Member';
    if (raw === 0) return 'Member';
  }
  const s = typeof raw === 'string' ? raw.trim() : '';
  if (!s) return fallback;
  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (n === 1) return 'Owner';
    if (n === 2) return 'Admin';
    if (n === 3) return 'Member';
    if (n === 0) return 'Member';
  }
  const r = s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (
    r === 'owner' ||
    r === 'proprietario' ||
    r.endsWith('.owner') ||
    r.endsWith('_owner')
  ) {
    return 'Owner';
  }
  if (
    r === 'admin' ||
    r === 'administrator' ||
    r === 'administrador' ||
    r === 'superadmin' ||
    r === 'companyadmin' ||
    r === 'tenantadmin' ||
    r.endsWith('.admin') ||
    r.endsWith('_admin')
  ) {
    return 'Admin';
  }
  return 'Member';
}

const COMPANY_ROLE_RANK: Record<CompanyMembershipRole, number> = {
  Member: 0,
  Admin: 1,
  Owner: 2,
};

/** Para reconciliar JWT, `/me` e membership sem rebaixar por fonte atrasada. */
export function pickStrongestCompanyRole(
  a: CompanyMembershipRole,
  b: CompanyMembershipRole
): CompanyMembershipRole {
  return COMPANY_ROLE_RANK[a] >= COMPANY_ROLE_RANK[b] ? a : b;
}

/**
 * Claim único ou lista de roles (ex.: JWT). `fallback` quando vazio ou inválido.
 */
export function normalizeCompanyRoleFromClaim(
  raw: unknown,
  fallback: CompanyMembershipRole = 'Member'
): CompanyMembershipRole {
  if (raw === undefined || raw === null) return fallback;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return fallback;
    let best: CompanyMembershipRole = fallback;
    for (const x of raw) {
      const n = normalizeCompanyMembershipRole(x, fallback);
      if (COMPANY_ROLE_RANK[n] > COMPANY_ROLE_RANK[best]) best = n;
    }
    return best;
  }
  return normalizeCompanyMembershipRole(raw, fallback);
}
